import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import {
  XCircleIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  MapPinIcon,
  BuildingOffice2Icon,
  ChatBubbleBottomCenterTextIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";

export default function LeadForm({ onClose, refreshLeads, lead }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: "Personne Physique",
    nom: "",
    prenom: "",
    telephone: "",
    email: "",
    linkedin: "",
    statutJuridique: "",
    siret: "",
    secteurActivite: "",
    adresse: "",
    sourceAcquisition: "",
    commentaire: "",
    statut: "Lead",
  });
  const [notification, setNotification] = useState({ show: false, message: "", type: "success" });

  const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: "", type: "success" }), 3000);
  };

  useEffect(() => {
    if (lead) setFormData((prev) => ({ ...prev, ...lead }));
  }, [lead]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    setLoading(true);

    // ✅ Vérification des champs obligatoires
    if (!formData.nom || !formData.email) {
      showToast("Merci de remplir au moins le nom et l'email.", "error");
      setLoading(false);
      return;
    }

    try {
      if (lead) {
        await axios.put(`${BACKEND_URL}/leads/${lead._id}`, formData);
        showToast("✅ Lead modifié avec succès !");
      } else {
        await axios.post(`${BACKEND_URL}/leads`, formData);
        showToast("✅ Nouveau lead ajouté !");
      }

      refreshLeads();
      showToast("🔄 Liste des leads mise à jour !");
      onClose();
    } catch (err) {
      console.error("❌ Erreur lors de l'enregistrement :", err);

      if (err.response?.status === 400) {
        showToast("Erreur de validation : certains champs sont invalides.", "error");
      } else if (err.response?.status === 500) {
        showToast("Erreur serveur : veuillez réessayer plus tard.", "error");
      } else {
        showToast("Erreur inconnue : contactez le support.", "error");
      }
    } finally {
      setLoading(false);
    }
  };  

  const isPM = formData.type === "Personne Morale";

  const steps = isPM
    ? [
        { label: "Identité", icon: <UserIcon className="w-5 h-5 inline mr-2" /> },
        { label: "Coordonnées", icon: <PhoneIcon className="w-5 h-5 inline mr-2" /> },
        { label: "Entreprise", icon: <BuildingOffice2Icon className="w-5 h-5 inline mr-2" /> },
        { label: "Adresse", icon: <MapPinIcon className="w-5 h-5 inline mr-2" /> },
        { label: "Commentaires", icon: <ChatBubbleBottomCenterTextIcon className="w-5 h-5 inline mr-2" /> },
      ]
    : [
        { label: "Identité", icon: <UserIcon className="w-5 h-5 inline mr-2" /> },
        { label: "Coordonnées", icon: <PhoneIcon className="w-5 h-5 inline mr-2" /> },
        { label: "Adresse", icon: <MapPinIcon className="w-5 h-5 inline mr-2" /> },
        { label: "Commentaires", icon: <ChatBubbleBottomCenterTextIcon className="w-5 h-5 inline mr-2" /> },
      ];

  const stepFields = {
    1: (
      <>
        <div>
          <label className="text-sm font-semibold">Type *</label>
          <select name="type" value={formData.type} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1">
            <option value="Personne Physique">Personne Physique</option>
            <option value="Personne Morale">Personne Morale</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold">Nom *</label>
          <input name="nom" value={formData.nom} onChange={handleChange} required className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        {formData.type === "Personne Physique" && (
          <div>
            <label className="text-sm font-semibold">Prénom</label>
            <input name="prenom" value={formData.prenom} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
          </div>
        )}
      </>
    ),
    2: (
      <>
        <div>
          <label className="text-sm font-semibold">Email *</label>
          <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        <div>
          <label className="text-sm font-semibold">Téléphone</label>
          <input name="telephone" value={formData.telephone} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        <div>
          <label className="text-sm font-semibold">LinkedIn</label>
          <input name="linkedin" value={formData.linkedin} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
      </>
    ),
    3: isPM ? (
      <>
        <div>
          <label className="text-sm font-semibold">Statut Juridique</label>
          <input name="statutJuridique" value={formData.statutJuridique} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        <div>
          <label className="text-sm font-semibold">SIRET</label>
          <input name="siret" value={formData.siret} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        <div>
          <label className="text-sm font-semibold">Secteur d’activité</label>
          <input name="secteurActivite" value={formData.secteurActivite} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
      </>
    ) : (
      <>
        <div>
          <label className="text-sm font-semibold">Adresse</label>
          <input name="adresse" value={formData.adresse} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        <div>
          <label className="text-sm font-semibold">Source d’acquisition</label>
          <input name="sourceAcquisition" value={formData.sourceAcquisition} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
      </>
    ),
    4: isPM ? (
      <>
        <div>
          <label className="text-sm font-semibold">Adresse</label>
          <input name="adresse" value={formData.adresse} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
        <div>
          <label className="text-sm font-semibold">Source d’acquisition</label>
          <input name="sourceAcquisition" value={formData.sourceAcquisition} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
      </>
    ) : (
      <>
        <div>
          <label className="text-sm font-semibold">Commentaire</label>
          <textarea name="commentaire" value={formData.commentaire} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
        </div>
      </>
    ),
    5: (
      <div>
        <label className="text-sm font-semibold">Commentaire</label>
        <textarea name="commentaire" value={formData.commentaire} onChange={handleChange} className="w-full border rounded px-3 py-2 mt-1" />
      </div>
    ),
  };

  return (
    <Dialog open={true} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white rounded-lg shadow-lg w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
          {/* ❌ Fermeture */}
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-gray-900">
            <XCircleIcon className="w-6 h-6" />
          </button>

          {/* 🧭 Étape + titre */}
          <DialogTitle className="text-xl font-semibold text-center mb-4">
            {steps[step - 1].icon}
            Étape {step} : {steps[step - 1].label}
          </DialogTitle>

          {/* 🔵 Barre de progression */}
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6">
            <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${(step / steps.length) * 100}%` }} />
          </div>

          {/* 🧾 Champs dynamiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{stepFields[step]}</div>

          {/* Navigation */}
          <div className="flex justify-between mt-6">
            <button disabled={step === 1} onClick={() => setStep((s) => s - 1)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg">
              Précédent
            </button>
            {step < steps.length ? (
              <button onClick={() => setStep((s) => s + 1)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">
                Suivant
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg">
                {loading ? "Enregistrement..." : lead ? "Modifier" : "Ajouter"}
              </button>
            )}
          </div>
        </DialogPanel>
      </div>
      <AnimatePresence>
        {notification.show && (
          <motion.div
            initial={{ opacity: 0, x: 100, y: 50 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 100, y: 50 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-6 right-6 z-50 w-full max-w-sm"
          >
            <div className={`rounded-lg shadow-lg p-4 flex items-start gap-3 border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-500"
                : "bg-red-50 border-red-500"
            }`}>
              <div className="shrink-0">
                {notification.type === "success" ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                ) : (
                  <XCircleIcon className="h-6 w-6 text-red-500" />
                )}
              </div>
              <div className="flex-1 text-sm text-gray-700">
                <p className="font-semibold">
                  {notification.type === "success" ? "Succès" : "Erreur"}
                </p>
                <p className="text-sm">{notification.message}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
}
